import { useMemo } from 'react'
import { useLocation, matchPath } from 'react-router-dom'
import { sections } from 'app/navigation/sections'
import { shouldRenderNavigation } from 'app/navigation/shouldRenderNavigation'
import { useSelector } from 'react-redux'
import { allFlags } from 'app/ducks/featureFlags/selectors'

// A rough "which area of the app is this" grouping: the first path
// segment (`/user/list` -> `user`, `/user-financial-documents` ->
// `user-financial-documents`). Used only to decide which section's
// icon should read as active for sub-pages that aren't themselves a
// tab (e.g. `/user/details/:id`) — the exact tab highlight below still
// matches on the full path.
const firstSegment = (path) => (path || '').split('/')[1]

// Resolves the sections/tabs the current user can see on this tenant,
// and — from the current URL — which section and which of its tabs is
// active. Powers both `SectionRail` and `SectionTopBar`.
export const useSectionNav = () => {
  const { pathname } = useLocation()
  // Sections/tabs are gated on feature flags (`shouldRenderNavigation`
  // reads the store), so recompute whenever they're (re)fetched.
  const featureFlags = useSelector(allFlags)

  return useMemo(() => {
    const visibleSections = sections
      .filter((section) => shouldRenderNavigation(section))
      .map((section) => ({
        ...section,
        children: section.children.filter((child) =>
          shouldRenderNavigation(child)
        ),
      }))
      .filter((section) => section.children.length > 0)

    const activeSection = visibleSections.find((section) =>
      section.children.some(
        (child) =>
          child.type !== 'extLink' &&
          firstSegment(child.path) === firstSegment(pathname)
      )
    )

    const activeChild = activeSection?.children.find(
      (child) =>
        child.type !== 'extLink' &&
        matchPath(pathname, { path: child.path, exact: child.exact })
    )

    return { sections: visibleSections, activeSection, activeChild }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, featureFlags])
}
